import React, { useEffect, useRef } from "react";
import { useSubscription } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import {
  updateUserSyncLog,
  removeSyncingEmployee,
  selectSyncingEmployeeIds,
  RemoteSyncLog,
} from "../slices/adminUsersSlice";
import {
  ON_REMOTE_SYNC_LOG_SUBSCRIPTION,
} from "../../graphql/subscriptions/onRemoteSyncLog/onRemoteSyncLogSubscription";
import { RemoteSyncLogSubscriptionData } from "../../graphql/subscriptions/onRemoteSyncLog/types";

interface EmployeeSyncSubscriptionProps {
  employeeId: number;
}

/**
 * Component that subscribes to a single employee's sync log and updates Redux.
 * Renders nothing - just handles the subscription side effect.
 */
const EmployeeSyncSubscription: React.FC<EmployeeSyncSubscriptionProps> = ({
  employeeId,
}) => {
  const dispatch = useDispatch();
  const hasSeenInProgress = useRef(false);

  const { data } = useSubscription<RemoteSyncLogSubscriptionData>(
    ON_REMOTE_SYNC_LOG_SUBSCRIPTION,
    {
      variables: { employeeId },
    }
  );

  useEffect(() => {
    if (data?.remote_sync_log?.[0]) {
      const log = data.remote_sync_log[0];

      const syncLog: RemoteSyncLog = {
        id: log.id,
        status: log.status as RemoteSyncLog["status"],
        log: log.log,
        syncResults: log.sync_results as RemoteSyncLog["syncResults"],
        startedAt: log.started_at,
        completedAt: log.completed_at,
      };

      // Update the user's sync log in Redux
      dispatch(updateUserSyncLog({ employeeId, syncLog }));

      // Track when we've seen the sync actually start
      if (log.status === "in_progress") {
        hasSeenInProgress.current = true;
      }

      // Only auto-remove after we've seen in_progress transition to success/failed
      // This prevents removing on stale data from a previous sync
      if (
        hasSeenInProgress.current &&
        (log.status === "success" || log.status === "failed")
      ) {
        dispatch(removeSyncingEmployee(employeeId));
      }
    }
  }, [data, employeeId, dispatch]);

  return null;
};

/**
 * Component that renders subscription handlers for all syncing employees.
 * Place this in AdminUserList to enable real-time sync status updates.
 */
export const EmployeeSyncSubscriptions: React.FC = () => {
  const syncingEmployeeIds = useSelector(selectSyncingEmployeeIds);

  return (
    <>
      {syncingEmployeeIds.map((employeeId) => (
        <EmployeeSyncSubscription key={employeeId} employeeId={employeeId} />
      ))}
    </>
  );
};
